import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import classnames from 'classnames';

import {
    Typography, Table, TableHead, TableBody, TableRow, TableCell, Input, Hidden, IconButton, TextField,
} from '@material-ui/core';
import { AddIcon } from '../../../../common/icons';
// import { with2dArrowNavigation } from '../accessibility/ArrowNavigation';
import { hasValue } from '../../../objectUtils';

const useStyles = makeStyles(theme => ({
    root: {
        backgroundColor: theme.palette.common.white,
    },
    desktopTable: {
        '&$fixedWidth': {
            overflowX: 'auto',
            paddingBottom: theme.spacing(4),
        },
        '&$fixedHeight': {
            overflowY: 'auto',
            height: 600,
        },
    },
    fixedWidth: {
    },
    fixedHeight: {

    },
    table: {
        tableLayout: 'fixed',

        '$fixedWidth &': {
            tableLayout: 'auto',
            width: 1200,
        },
        '$fixedHeight &': {
            tableLayout: 'auto',
        },
    },
    mobileTable: {

    },
    mobileRow: {
        marginBottom: theme.spacing(2),
        border: `1px solid ${theme.palette.grey[400]}`,
        backgroundColor: theme.palette.grey[200],
    },
    mobileRowHeader: {
        padding: theme.spacing(1),
        backgroundColor: theme.palette.primary.light,
        color: theme.palette.common.white,
    },
    mobileCell: {
        marginBottom: theme.spacing(2),
        padding: theme.spacing(1),
    },
    tableRow: {
        height: theme.spacing(4),
    },
    tableHeaderRow: {
    },
    tableHeaderCell: {
        backgroundColor: theme.palette.grey[200],
        border: 0,
        borderRight: `1px solid ${theme.palette.grey[200]}`,
        fontSize: '1.1em',
        color: theme.palette.grey[900],
        padding: `0 ${theme.spacing(1)}px`,
        overflowWrap: 'anywhere',

        '&:last-child': {
            borderRight: `1px solid ${theme.palette.grey[300]}`,
            padding: `0 ${theme.spacing(1)}px`,
        },

        '$fixedWidth &': {
            overflowWrap: 'normal',
        },
        '$fixedHeight &': {
            position: 'sticky',
            top: 0,
            zIndex: 50,
        },
    },
    rowHeaderCell: {
        backgroundColor: theme.palette.grey[200],
    },
    tableCell: {
        padding: `0 ${theme.spacing(1)}px`,
        paddingTop: theme.spacing(1),
        verticalAlign: 'top',
        borderRight: `1px solid ${theme.palette.grey[300]}`,

        '&:first-child': {
            borderLeft: `1px solid ${theme.palette.grey[300]}`,
        },
        '&:last-child': {
            padding: `0 ${theme.spacing(1)}px`,
            paddingTop: theme.spacing(1),
        },
    },
    multiQuestionCell: {
    },
    multiInput: {
        marginBottom: theme.spacing(0.5),
    },
    addRowContainer: {
        display: 'flex',
        justifyContent: 'center',
    },
    addRowButton: {
        marginTop: theme.spacing(1),
    },
    disabledReadonlyInput: {
        color: theme.palette.text.primary,
    },
}));


const padRow = (row, length) => row.concat(Array(length).fill('')).slice(0, length);

function TableResponse({
    tableConfig, response, disabled, handleResponseUpdate, translate, elementSettings, readOnly,
}) {
    const theme = useTheme();
    const classes = useStyles();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const [multiTableValues, setMultiTableValues] = React.useState({});
    const [updateIndex, setUpdateIndex] = React.useState({});
    const updateAsyncTimer = React.useRef();
    const [responseState, setResponseState] = React.useState({});

    const fixedNumberOfRows = tableConfig?.rowHeaders?.length;

    const emptyRows = () => {
        if (fixedNumberOfRows) {
            if (response?.length >= fixedNumberOfRows) {
                return [];
            }
            return [...Array(fixedNumberOfRows).keys()].map(() => tableConfig.columnHeaders.map(() => ''));
        }

        if (isMobile) {
            if (!response || response.length === 0) {
                return [tableConfig.columnHeaders.map(() => '')];
            }
            return [];
        }

        return [tableConfig.columnHeaders.map(() => '')];
    };

    const addRow = () => {
        const currResponse = [...response || [], ...emptyRows()];
        const addedResponse = [...currResponse, ...[tableConfig.columnHeaders.map(() => '')]];

        handleResponseUpdate(addedResponse);
    };

    const handleTableCellChange = (rowIndex, cellIndex) => (event) => {
        if (readOnly) return;

        let prevResponse = response || [];
        if (rowIndex >= prevResponse.length) {
            prevResponse = [...prevResponse, ...emptyRows()];
        }

        handleResponseUpdate(prevResponse.map((row, ri) => (
            ri === rowIndex ? padRow(row, tableConfig.columnHeaders.length).map((cell, ci) => (
                ci === cellIndex ? event.target.value : cell
            )) : row
        )));

        // Fix for text-fields. To avoid loosing marker-position value is copied to internal state on first change.
        // textfield has prop value={this.state.freetextResponse || response?.freetextResponse || ''}
        setResponseState(prevState => ({ ...prevState, [`${rowIndex}-${cellIndex}`]: event.target.value }));
    };

    const handleTableMultiCellChange = (rowIndex, cellIndex, questionIndex, curr) => (event) => {
        if (readOnly) return;

        const val = event.target.value;
        setMultiTableValues(prev => ({
            ...prev,
            [`${rowIndex}-${cellIndex}`]: {
                ...curr,
                ...prev && prev[`${rowIndex}-${cellIndex}`],
                [questionIndex]: val,
            },
        }));
        setUpdateIndex({ rowIndex, cellIndex });
    };

    React.useEffect(() => {
        if (hasValue(updateIndex)) {
            clearTimeout(updateAsyncTimer.current);
            updateAsyncTimer.current = setTimeout(() => {
                handleTableCellChange(updateIndex.rowIndex, updateIndex.cellIndex)({
                    target: { value: JSON.stringify(multiTableValues[`${updateIndex.rowIndex}-${updateIndex.cellIndex}`]) },
                });
            }, 200);
        }
    // Only fire on updateIndex-change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateIndex]);

    if (!tableConfig?.columnHeaders) {
        return null;
    }

    const getCellValue = (rowIndex, cellIndex, questionIndex, cell) => (multiTableValues[`${rowIndex}-${cellIndex}`] && multiTableValues[`${rowIndex}-${cellIndex}`][questionIndex])
            || (cell && JSON.parse(cell)[questionIndex])
            || '';

    const renderCells = (ri, ci, cell, mobile) => (
        tableConfig.cellQuestions ? (
            <div className={classes.multiQuestionCell}>
                {tableConfig.cellQuestions.map((q, qi) => (
                    <TextField
                    // eslint-disable-next-line react/no-array-index-key
                        key={qi}
                        className={classes.multiInput}
                        disabled={disabled || readOnly}
                        // id={`tableResponse${ri}${ci}${qi}`}
                        label={q}
                        value={getCellValue(ri, ci, qi, cell)}
                        onChange={handleTableMultiCellChange(ri, ci, qi, cell && JSON.parse(cell))}
                        fullWidth
                        inputProps={{ autoComplete: 'off' }}
                        classes={{ disabled: readOnly ? classes.disabledReadonlyInput : null }}
                    />
                ))}
            </div>
        ) : (
            <Input
                // id={`tableResponse${ri}${ci}`}
                disabled={disabled || readOnly}
                value={responseState[`${ri}-${ci}`] || cell}
                onChange={handleTableCellChange(ri, ci)}
                classes={{ disabled: readOnly ? classes.disabledReadonlyInput : null }}
                multiline
                minRows={1}
                maxRows={10}
                fullWidth
                disableUnderline={!mobile}
                inputProps={{ autoComplete: 'off' }}
                // inputRef={navigation.setRef(ri, ci)}
                // inputProps={{
                //     onKeyDown: navigation.handleKeyDown,
                //     onMouseDown: navigation.handleMouseDown(ri, ci),
                //     tabIndex: -1,
                //     ...ri === 0 && ci === 0 && {
                //         tabIndex: 0,
                //         onFocus: navigation.handleMouseDown(ri, ci),
                //     },
                // }}
            />
        )
    );

    const tableResponses = [...response || [], ...emptyRows()];

    return (
        <div className={classes.root}>
            <Hidden implementation="js" xsDown>
                <div className={classnames(classes.desktopTable, { [classes.fixedWidth]: tableConfig.wide, [classes.fixedHeight]: tableConfig.fixedHeight })}>
                    <Table className={classes.table}>
                        <TableHead className={classes.tableHeaderRow}>
                            <TableRow>
                                {tableConfig.rowHeaders && <TableCell className={classes.tableHeaderCell} style={{ width: Number(elementSettings?.firstColWidth) || '' }} />}
                                {tableConfig.columnHeaders.map(item => (
                                    <TableCell key={item} padding="none" className={classes.tableHeaderCell}>
                                        {item}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {tableResponses?.map((row, ri) => (
                            // eslint-disable-next-line react/no-array-index-key
                                <TableRow key={ri} className={classes.tableRow}>
                                    {tableConfig.rowHeaders && (
                                        <TableCell className={classes.rowHeaderCell}>
                                            {tableConfig.rowHeaders[ri]}
                                        </TableCell>
                                    )}
                                    {padRow(row, tableConfig.columnHeaders.length).map((cell, ci) => (
                                    // eslint-disable-next-line react/no-array-index-key
                                        <TableCell key={ci} padding="none" className={classes.tableCell}>
                                            {renderCells(ri, ci, cell)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            </Hidden>

            <Hidden implementation="js" smUp>
                <div className={classes.mobileTable}>
                    {tableResponses?.map((row, ri) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div className={classes.mobileRow} key={ri}>
                            <div className={classes.mobileRowHeader}>
                                <Typography variant="subtitle1" color="inherit">
                                    {tableConfig.rowHeaders
                                        ? tableConfig.rowHeaders[ri]
                                        : `${ri + 1}.`
                                    }
                                </Typography>
                            </div>
                            {row.map((cell, ci) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <div className={classes.mobileCell} key={ci}>
                                    <div className={classes.mobileCellHeader}>
                                        <Typography variant="subtitle2">
                                            {`${tableConfig.columnHeaders[ci]}:`}
                                        </Typography>
                                    </div>
                                    {renderCells(ri, ci, cell, true)}
                                </div>
                            ))}
                        </div>
                    ))}

                    {!fixedNumberOfRows && !readOnly && (
                        <div className={classes.addRowContainer}>
                            <IconButton onClick={addRow} color="primary" className={classes.addRowButton} aria-label={translate('add')}>
                                <AddIcon />
                            </IconButton>
                        </div>
                    )}

                </div>
            </Hidden>
        </div>
    );
}

TableResponse.propTypes = {
    // navigation: PropTypes.object.isRequired, // with2dArrowNavigation
    translate: PropTypes.func.isRequired,
    handleResponseUpdate: PropTypes.func.isRequired,
    tableConfig: PropTypes.object,
    response: PropTypes.array,
    disabled: PropTypes.bool,
    readOnly: PropTypes.bool,
    elementSettings: PropTypes.object,
};

TableResponse.defaultProps = {
    response: null,
    disabled: false,
    readOnly: false,
    tableConfig: {},
    elementSettings: null,
};

const mapStateToProps = state => ({
    translate: getTranslate(state.localize),
});

// export default connect(mapStateToProps)(with2dArrowNavigation(TableResponse));
export default connect(mapStateToProps)(TableResponse);
